/**
 * PlaybookSuggestionPanel - Context-aware negotiation suggestions
 *
 * Displays relevant scripts, objection responses, and power phrases
 * based on the current deal context (financing type, seller situation, etc.)
 */

import React, { useState } from 'react';
import {
  Phone,
  Shield,
  DollarSign,
  Target,
  Zap,
  ChevronDown,
  ChevronRight,
  Copy,
  Check,
  RefreshCw,
  Lightbulb,
  MessageSquare,
  Settings,
} from 'lucide-react';
import { usePlaybookSuggestions, useLogPlaybookUsage } from '../../hooks/usePlaybook';
import { usePropertyMotivations } from '../../hooks/usePropertyMotivations';
import { MotivationSelector } from './MotivationSelector';
import type {
  PlaybookSuggestion,
  ContextSuggestionsRequest,
  InferredMotivation,
} from '../../types/playbook';
import { MOTIVATION_SIGNAL_LABELS } from '../../types/playbook';

interface PlaybookSuggestionPanelProps {
  financingType?: string;
  dealType?: string;
  daysOnMarket?: number;
  priceReductionCount?: number;
  isForeclosure?: boolean;
  isVacant?: boolean;
  hasLowEquity?: boolean;
  propertyId?: number;
  className?: string;
}

const ENTRY_TYPE_CONFIG: Record<string, {
  icon: React.ElementType;
  label: string;
  color: string;
  bgColor: string;
}> = {
  opening_script: {
    icon: Phone,
    label: 'Opening Scripts',
    color: 'text-emerald-400',
    bgColor: 'bg-emerald-500/10',
  },
  objection_response: {
    icon: Shield,
    label: 'Objection Handlers',
    color: 'text-amber-400',
    bgColor: 'bg-amber-500/10',
  },
  seller_finance_pitch: {
    icon: DollarSign,
    label: 'Seller Finance Pitches',
    color: 'text-blue-400',
    bgColor: 'bg-blue-500/10',
  },
  closing_technique: {
    icon: Target,
    label: 'Closing Techniques',
    color: 'text-purple-400',
    bgColor: 'bg-purple-500/10',
  },
  power_phrase: {
    icon: Zap,
    label: 'Power Phrases',
    color: 'text-yellow-400',
    bgColor: 'bg-yellow-500/10',
  },
};

function SuggestionCard({
  suggestion,
  propertyId: _propertyId,
  onCopy,
}: {
  suggestion: PlaybookSuggestion;
  propertyId?: number;
  onCopy: (text: string, id: string) => void;
}) {
  const [copied, setCopied] = useState(false);
  const config = ENTRY_TYPE_CONFIG[suggestion.type] || ENTRY_TYPE_CONFIG.power_phrase;
  const Icon = config.icon;

  const handleCopy = () => {
    onCopy(suggestion.content, suggestion.id);
    setCopied(true);
    navigator.clipboard.writeText(suggestion.content);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <div className="group relative bg-slate-800/50 rounded-lg p-3 hover:bg-slate-800 transition-colors">
      <div className="flex items-start gap-3">
        <div className={`p-1.5 rounded ${config.bgColor}`}>
          <Icon className={`w-4 h-4 ${config.color}`} />
        </div>
        <div className="flex-1 min-w-0">
          {suggestion.objection && (
            <div className="text-sm text-amber-400/80 mb-1 font-medium">
              When they say: "{suggestion.objection}"
            </div>
          )}
          <p className="text-sm text-slate-200 leading-relaxed">
            "{suggestion.content}"
          </p>
        </div>
        <button
          onClick={handleCopy}
          className="opacity-0 group-hover:opacity-100 transition-opacity p-1.5 hover:bg-slate-700 rounded"
          title="Copy to clipboard"
        >
          {copied ? (
            <Check className="w-4 h-4 text-emerald-400" />
          ) : (
            <Copy className="w-4 h-4 text-slate-400" />
          )}
        </button>
      </div>
    </div>
  );
}

function SuggestionSection({
  title,
  icon: Icon,
  color,
  bgColor,
  suggestions,
  propertyId,
  onCopy,
  defaultExpanded = false,
}: {
  title: string;
  icon: React.ElementType;
  color: string;
  bgColor: string;
  suggestions: PlaybookSuggestion[];
  propertyId?: number;
  onCopy: (text: string, id: string) => void;
  defaultExpanded?: boolean;
}) {
  const [expanded, setExpanded] = useState(defaultExpanded);

  if (suggestions.length === 0) return null;

  return (
    <div className="border border-slate-700 rounded-lg overflow-hidden">
      <button
        onClick={() => setExpanded(!expanded)}
        className="w-full flex items-center justify-between p-3 bg-slate-800/50 hover:bg-slate-800 transition-colors"
      >
        <div className="flex items-center gap-2">
          <div className={`p-1.5 rounded ${bgColor}`}>
            <Icon className={`w-4 h-4 ${color}`} />
          </div>
          <span className="font-medium text-slate-200">{title}</span>
          <span className="text-sm text-slate-500">({suggestions.length})</span>
        </div>
        {expanded ? (
          <ChevronDown className="w-4 h-4 text-slate-400" />
        ) : (
          <ChevronRight className="w-4 h-4 text-slate-400" />
        )}
      </button>
      {expanded && (
        <div className="p-3 space-y-2 bg-slate-900/30">
          {suggestions.map((suggestion) => (
            <SuggestionCard
              key={suggestion.id}
              suggestion={suggestion}
              propertyId={propertyId}
              onCopy={onCopy}
            />
          ))}
        </div>
      )}
    </div>
  );
}

export default function PlaybookSuggestionPanel({
  financingType,
  dealType,
  daysOnMarket,
  priceReductionCount,
  isForeclosure = false,
  isVacant = false,
  hasLowEquity = false,
  propertyId,
  className = '',
}: PlaybookSuggestionPanelProps) {
  const logUsage = useLogPlaybookUsage();
  const [showMotivationSelector, setShowMotivationSelector] = useState(false);

  // Fetch property motivations (manual + inferred from dedicated API)
  const {
    motivations: propertyMotivations,
    updateMotivations,
    isUpdating,
  } = usePropertyMotivations(propertyId);

  // Build context for suggestions
  const context: ContextSuggestionsRequest = {
    property_id: propertyId,  // Enable auto-inference when property_id provided
    financing_type: financingType,
    deal_type: dealType,
    days_on_market: daysOnMarket,
    price_reduction_count: priceReductionCount,
    is_foreclosure: isForeclosure,
    is_vacant: isVacant,
    has_low_equity: hasLowEquity,
    limit_per_type: 5,
  };

  // Only fetch if we have a relevant context
  const shouldFetch = financingType === 'seller_finance' || financingType === 'SELLER';

  const { data, isLoading, error, refetch } = usePlaybookSuggestions(context, shouldFetch);

  const handleCopy = (_text: string, entryId: string) => {
    logUsage.mutate({
      entry_id: entryId,
      action: 'copied',
      property_id: propertyId,
      context: { financing_type: financingType, deal_type: dealType },
    });
  };

  // Don't render if not seller financing
  if (!shouldFetch) {
    return null;
  }

  if (isLoading) {
    return (
      <div className={`bg-slate-900 rounded-xl p-4 ${className}`}>
        <div className="flex items-center gap-2 mb-4">
          <MessageSquare className="w-5 h-5 text-amber-400" />
          <h3 className="font-semibold text-slate-200">Negotiation Playbook</h3>
        </div>
        <div className="animate-pulse space-y-3">
          <div className="h-10 bg-slate-800 rounded" />
          <div className="h-10 bg-slate-800 rounded" />
          <div className="h-10 bg-slate-800 rounded" />
        </div>
      </div>
    );
  }

  if (error || !data) {
    return (
      <div className={`bg-slate-900 rounded-xl p-4 ${className}`}>
        <div className="flex items-center gap-2 mb-4">
          <MessageSquare className="w-5 h-5 text-amber-400" />
          <h3 className="font-semibold text-slate-200">Negotiation Playbook</h3>
        </div>
        <div className="text-sm text-slate-400 text-center py-4">
          Unable to load suggestions
          <button
            onClick={() => refetch()}
            className="ml-2 text-amber-400 hover:text-amber-300"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  const hasSuggestions = data.total_suggestions > 0;

  return (
    <div className={`bg-slate-900 rounded-xl p-4 ${className}`}>
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <MessageSquare className="w-5 h-5 text-amber-400" />
          <h3 className="font-semibold text-slate-200">Negotiation Playbook</h3>
          <span className="text-sm text-slate-500 bg-slate-800 px-2 py-0.5 rounded">
            {data.total_suggestions} suggestions
          </span>
        </div>
        <div className="flex items-center gap-2">
          {propertyId && (
            <button
              onClick={() => setShowMotivationSelector(!showMotivationSelector)}
              className={`flex items-center gap-1 px-2 py-1 text-sm rounded transition-colors ${
                showMotivationSelector
                  ? 'bg-blue-500/20 text-blue-400'
                  : 'hover:bg-slate-800 text-slate-400'
              }`}
              title="Edit seller motivation signals"
            >
              <Settings className="w-3 h-3" />
              {showMotivationSelector ? 'Hide' : 'Edit'} Signals
            </button>
          )}
          <button
            onClick={() => refetch()}
            className="p-1.5 hover:bg-slate-800 rounded transition-colors"
            title="Refresh suggestions"
          >
            <RefreshCw className="w-4 h-4 text-slate-400" />
          </button>
        </div>
      </div>

      {/* Manual Motivation Selector (expandable) */}
      {showMotivationSelector && propertyId && (
        <MotivationSelector
          propertyId={propertyId}
          selectedMotivations={propertyMotivations?.manual_motivations || []}
          inferredMotivations={propertyMotivations?.inferred_motivations}
          onMotivationsChange={updateMotivations}
          isUpdating={isUpdating}
          className="mb-4"
        />
      )}

      {/* Inferred Motivations with Confidence */}
      {data.inferred_motivations && data.inferred_motivations.length > 0 ? (
        <div className="mb-4 p-3 bg-slate-800/50 rounded-lg">
          <div className="flex items-center justify-between mb-2">
            <p className="text-sm text-slate-400">Detected Seller Signals:</p>
            {data.motivation_level && (
              <span className={`text-sm px-2 py-0.5 rounded ${
                data.motivation_level === 'high'
                  ? 'bg-emerald-500/20 text-emerald-400'
                  : data.motivation_level === 'medium'
                  ? 'bg-amber-500/20 text-amber-400'
                  : 'bg-slate-600/50 text-slate-400'
              }`}>
                {data.motivation_level.toUpperCase()} Motivation
              </span>
            )}
          </div>
          <div className="flex flex-wrap gap-2">
            {data.inferred_motivations.map((m: InferredMotivation, idx: number) => (
              <div
                key={`${m.signal}-${idx}`}
                className="flex items-center gap-1 text-sm bg-amber-500/20 text-amber-400 px-2 py-1 rounded"
                title={`${m.source}${m.value ? ` (${m.value})` : ''}`}
              >
                <span>{MOTIVATION_SIGNAL_LABELS[m.signal] || m.signal}</span>
                <span className="opacity-60">{(m.confidence * 100).toFixed(0)}%</span>
              </div>
            ))}
          </div>
        </div>
      ) : (
        /* Fallback to manual props when no inference */
        <div className="flex flex-wrap gap-2 mb-4">
          {financingType === 'seller_finance' || financingType === 'SELLER' ? (
            <span className="text-sm bg-blue-500/20 text-blue-400 px-2 py-1 rounded">
              Seller Finance
            </span>
          ) : null}
          {isForeclosure && (
            <span className="text-sm bg-red-500/20 text-red-400 px-2 py-1 rounded">
              Foreclosure
            </span>
          )}
          {hasLowEquity && (
            <span className="text-sm bg-amber-500/20 text-amber-400 px-2 py-1 rounded">
              Low Equity
            </span>
          )}
          {isVacant && (
            <span className="text-sm bg-cyan-500/20 text-cyan-400 px-2 py-1 rounded">
              Vacant
            </span>
          )}
          {daysOnMarket && daysOnMarket > 60 && (
            <span className="text-sm bg-purple-500/20 text-purple-400 px-2 py-1 rounded">
              {daysOnMarket} DOM
            </span>
          )}
        </div>
      )}

      {hasSuggestions ? (
        <div className="space-y-3">
          {/* Seller Finance Pitches - most relevant for this context */}
          <SuggestionSection
            title="Seller Finance Pitches"
            icon={DollarSign}
            color="text-blue-400"
            bgColor="bg-blue-500/10"
            suggestions={data.seller_finance_pitches}
            propertyId={propertyId}
            onCopy={handleCopy}
            defaultExpanded={true}
          />

          {/* Opening Scripts */}
          <SuggestionSection
            title="Opening Scripts"
            icon={Phone}
            color="text-emerald-400"
            bgColor="bg-emerald-500/10"
            suggestions={data.opening_scripts}
            propertyId={propertyId}
            onCopy={handleCopy}
          />

          {/* Objection Handlers */}
          <SuggestionSection
            title="Objection Handlers"
            icon={Shield}
            color="text-amber-400"
            bgColor="bg-amber-500/10"
            suggestions={data.objection_responses}
            propertyId={propertyId}
            onCopy={handleCopy}
          />

          {/* Closing Techniques */}
          <SuggestionSection
            title="Closing Techniques"
            icon={Target}
            color="text-purple-400"
            bgColor="bg-purple-500/10"
            suggestions={data.closing_techniques}
            propertyId={propertyId}
            onCopy={handleCopy}
          />

          {/* Power Phrases */}
          <SuggestionSection
            title="Power Phrases"
            icon={Zap}
            color="text-yellow-400"
            bgColor="bg-yellow-500/10"
            suggestions={data.power_phrases}
            propertyId={propertyId}
            onCopy={handleCopy}
          />
        </div>
      ) : (
        <div className="text-center py-6 text-slate-400">
          <Lightbulb className="w-8 h-8 mx-auto mb-2 opacity-50" />
          <p className="text-sm">No suggestions available for this context</p>
        </div>
      )}

      {/* Source attribution */}
      <div className="mt-4 pt-3 border-t border-slate-800">
        <p className="text-sm text-slate-500 text-center">
          Extracted from @hold_my_hand_wholesale video content
        </p>
      </div>
    </div>
  );
}
